<template>
  <PlaceholderComponent v-if="shouldDisplayPlaceholderComponent(isEditMode, mode, visible)" :name="tracking_id" />
  <template v-else>
    <EditableComponent
      v-if="isEditMode && !isViewOnlyMode && componentPermissions.hasSomethingEditable"
      :id="id"
      :active="componentEditMode && componentEditMode?.id === id"
      :duplicate="componentPermissions.canDuplicate"
      :edit="componentPermissions.canEdit"
      :exclude-stylables="
        isScribbleChild
          ? [ThemeComponentOptionEnum.SPACING, ThemeComponentOptionEnum.PADDING, ThemeComponentOptionEnum.BORDER_RADIUS]
          : [ThemeComponentOptionEnum.COLOR_PICKER, ThemeComponentOptionEnum.PADDING]
      "
      :parent-type="parentType"
      :remove="componentPermissions.canRemove"
      :resizable="isScribbleChild && !usedInSectionId"
      :resize-handles="[
        'top-left',
        'top-right',
        'center-left',
        'center-right',
        'bottom-left',
        'bottom-center',
        'bottom-right',
      ]"
      :selected="componentSelectedMode && componentSelectedMode?.id === id"
      :settings="!usedInSectionId"
      :show-only-bottom-toolbar="shouldShowOnlyBottomToolbar"
      :show-only-top-toolbar="shouldShowOnlyTopToolbar"
      stylable
      @duplicate="duplicate(id)"
      @edit="setComponentEditMode(ComponentTypes.Embeddable, id)"
      @remove="removeComponentById(id)"
      @resize-end="handleUserResizeEnd"
      @resize-start="handleUserResizeStart"
      @select="setComponentSelectedMode(id)"
      @settings="setComponentSettingsMode(id)"
      @style="emit('style', $event)"
    >
      <div
        v-if="isDimensionsChanging"
        class="absolute inset-0 z-10"
        style="background: transparent; pointer-events: none"
      />
      <EmbeddableEdit v-if="!data?.src" :data="data" />
      <EmbeddableRaw
        v-else
        :id="id"
        :class="{ 'pointer-events-none': isDimensionsChanging }"
        :data="data"
        :used-in-section-id="usedInSectionId"
        :when-used-in-section="when_used_in_section"
        v-bind="$attrs"
        @iframe-wrapper-clicked="setComponentSelectedMode(id)"
      />
    </EditableComponent>
    <EmbeddableRaw
      v-else-if="data?.src && data?.embed_locations?.includes(currentEmbedLocation)"
      :id="id"
      :data="data"
      :used-in-section-id="usedInSectionId"
      :when-used-in-section="when_used_in_section"
      v-bind="$attrs"
      @iframe-wrapper-clicked="setComponentSelectedMode(id)"
    />
  </template>
</template>

<script lang="ts" setup>
import { toRefs, onMounted, onBeforeUnmount, watch, ref, type CSSProperties } from 'vue'

import EditableComponent from '@canvas-builder/components/container/EditableComponent/EditableComponent.vue'
import type { EmbeddableProps, ComponentNode } from '@canvas-builder/types/canvas'
import { ComponentTypes } from '@canvas-builder/types/canvas'
import useCanvas from '@canvas-builder/composables/useCanvas'
import EmbeddableRaw from './Embeddable.raw.vue'
import EmbeddableEdit from './Embeddable.edit.vue'
import { defaultWhenUsedInSection } from '@canvas-builder/constants'
import type { CanvasRetrieve } from '@openapi'
import useComponentPermissions from '@canvas-builder/composables/useComponentPermissions'
import { ThemeComponentOptionEnum } from '@canvas-builder/composables/useCanvasTheme'
import { shouldDisplayPlaceholderComponent } from '@canvas-builder/util/canvas.util'
import PlaceholderComponent from '@canvas-builder/components/container/PlaceholderComponent/PlaceholderComponent.vue'
import { useScribbleChild, getEmbeddableScribbleConfig } from '@canvas-builder/composables/useScribbleChild'
import { useScribbleToolbar } from '@canvas-builder/composables/useScribbleToolbar'

const props = withDefaults(
  defineProps<{
    id: string
    data: EmbeddableProps
    when_used_in_section?: ComponentNode['when_used_in_section']
    usedInSectionId?: CanvasRetrieve['id']
    visible?: ComponentNode['visible']
    tracking_id?: string
  }>(),
  {
    when_used_in_section: () => defaultWhenUsedInSection,
    usedInSectionId: '',
  },
)

const emit = defineEmits<{
  style: [s: CSSProperties]
}>()

const { id, usedInSectionId, when_used_in_section } = toRefs(props)
const componentPermissions = useComponentPermissions({
  id,
  usedInSectionId,
  when_used_in_section,
})

const {
  mode,
  isEditMode,
  removeComponentById,
  setComponentEditMode,
  componentEditMode,
  setComponentSelectedMode,
  componentSelectedMode,
  justAddedComponentId,
  duplicate,
  currentEmbedLocation,
  isViewOnlyMode,
  setComponentSettingsMode,
  updateNodeDataById,
  getParentType,
} = useCanvas()

const parentType = getParentType(id)
const scribbleChildConfig = getEmbeddableScribbleConfig(props.id, parentType.value, props.data, undefined)
const { isScribbleChild, handleAddedToScribble, componentNode } = useScribbleChild(scribbleChildConfig)
const { shouldShowOnlyTopToolbar, shouldShowOnlyBottomToolbar } = useScribbleToolbar(props.id)

// Track if dimensions are currently changing to prevent feedback loops
const isDimensionsChanging = ref(false)
const isUserResizing = ref(false)
const lastStyleUpdate = ref<string>('')

// Update component data dimensions when style changes
const updateDimensions = (newWidth?: string, newHeight?: string) => {
  if (isScribbleChild.value) {
    updateNodeDataById(props.id, {
      ...props.data,
      ...(newWidth != null && { width: newWidth }),
      ...(newHeight != null && { height: newHeight }),
    })
  }
}

watch(
  () => componentNode.value?.style,
  (newStyle, oldStyle) => {
    if (!isScribbleChild.value || !newStyle || !oldStyle) return

    const newWidth = newStyle.width
    const newHeight = newStyle.height
    const oldWidth = oldStyle.width
    const oldHeight = oldStyle.height

    // Check if dimensions changed
    const dimensionsChanged = newWidth !== oldWidth || newHeight !== oldHeight

    // Only proceed if dimensions actually changed and at least one has a valid value
    if (dimensionsChanged && (newWidth != null || newHeight != null)) {
      // Handle dimension changes (distinguishes between user resize and programmatic changes)
      handleDimensionsChange()

      // Create a key to track if this is a new change (handle null/undefined safely)
      const styleKey = `${newWidth ?? 'null'}-${newHeight ?? 'null'}`

      // Only update if it's not the same as last update
      if (styleKey !== lastStyleUpdate.value) {
        lastStyleUpdate.value = styleKey
        updateDimensions(
          typeof newWidth === 'string' ? newWidth : undefined,
          typeof newHeight === 'string' ? newHeight : undefined,
        )
      }
    }
  },
  { deep: true },
)

// Handle user-initiated resize events
function handleUserResizeStart() {
  isUserResizing.value = true
  isDimensionsChanging.value = true
}

function handleUserResizeEnd() {
  isUserResizing.value = false
  // Keep isDimensionsChanging true briefly to handle any final style updates
  setTimeout(() => {
    if (!isUserResizing.value) {
      isDimensionsChanging.value = false
    }
  }, 100)
}

// Handle dimension changes (from any source - user resize, programmatic updates, etc.)
let dimensionsChangeTimeoutId: number | null = null

function handleDimensionsChange() {
  // Only set isDimensionsChanging for programmatic changes
  // User resize changes are handled by the resize events above
  if (!isUserResizing.value) {
    isDimensionsChanging.value = true

    // Clear any existing timeout
    if (dimensionsChangeTimeoutId) {
      clearTimeout(dimensionsChangeTimeoutId)
    }

    // Set a timeout to detect when dimension changes end
    dimensionsChangeTimeoutId = window.setTimeout(() => {
      isDimensionsChanging.value = false
      dimensionsChangeTimeoutId = null
    }, 150) // 150ms after last dimension change
  }
}

onMounted(() => {
  if (justAddedComponentId.value === props.id) {
    justAddedComponentId.value = null
    handleAddedToScribble(emit)
    if (!props.data.app_name) setComponentEditMode(ComponentTypes.Embeddable, props.id)
  }
})

onBeforeUnmount(() => {
  // Clean up timeout
  if (dimensionsChangeTimeoutId) {
    clearTimeout(dimensionsChangeTimeoutId)
  }
})
</script>
